/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  forwardRef,
  ViewChild,
  EventEmitter,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";

// models
import { ICAutocompleteGenericModel } from "./models/bfx-interface-controls.models";
import { ICNInputControlState } from "./models/bfx-interface-controls.enums";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ObjectUtils } from "@bfx/primeng/utils";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";

export const ALGO_TYPE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICAlgoTypeComponent),
  multi: true,
};

@Component({
  selector: "ic-algo-type",
  templateUrl: "./html/ic-algo-type.html",
  providers: [ALGO_TYPE_VALUE_ACCESSOR],
  host: {
    class: "interface-control__custom-input-autocomplete__container",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICAlgoTypeComponent implements OnInit, OnDestroy, ControlValueAccessor {
  /**********************************************************************/
  /* DATA */

  @Input() label: any = "Type";

  @Input() entryType: OENEntryType = OENEntryType.SPOT;

  public get dataset(): Array<ICAutocompleteGenericModel> {
    return this.data.dataset;
  }

  @Input()
  public set dataset(value: Array<ICAutocompleteGenericModel>) {
    this.data.dataset = value;
    this.search({ query: "" });
    this.cd.markForCheck();
  }

  private _disabled: boolean = false;

  public get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  public set disabled(value: boolean) {
    this._disabled = value;
    this.cd.markForCheck();
  }

  @Input() allowEmpty: boolean = false;

  @Input() isLoading: boolean = false;

  public inputFocused: boolean = false;

  public locked: boolean = false;

  public data = new ICAlgoTypeComponent.Data(this);

  private static Data = class {
    cd: ChangeDetectorRef;
    constructor(public component: ICAlgoTypeComponent) {
      this.cd = component.cd;
    }
    private _value: ICAutocompleteGenericModel | string = null;
    public get value(): ICAutocompleteGenericModel | string {
      return this._value;
    }
    public set value(value: ICAutocompleteGenericModel | string) {
      this._value = value;
      this.component.onModelChange(value);
      this.cd.markForCheck();
    }
    private _dataset: Array<ICAutocompleteGenericModel> = [];
    public get dataset(): Array<ICAutocompleteGenericModel> {
      return this._dataset;
    }
    public set dataset(value: Array<ICAutocompleteGenericModel>) {
      this._dataset = value;
      this.cd.markForCheck();
    }
    private _filteredItems: Array<ICAutocompleteGenericModel> = [];
    public get filteredItems(): Array<ICAutocompleteGenericModel> {
      return this._filteredItems;
    }
    public set filteredItems(value: Array<ICAutocompleteGenericModel>) {
      this._filteredItems = value;
      this.cd.markForCheck();
    }
    private _state: ICNInputControlState = ICNInputControlState.INACTIVE;
    public get state(): ICNInputControlState {
      return this._state;
    }
    public set state(value: ICNInputControlState) {
      this._state = value;
      this.cd.markForCheck();
    }
  };

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  @ViewChild("inputField", { static: true })
  inputField: BfxAutoCompleteComponent;

  /**********************************************************************/
  /* External Validation */

  @Input()
  set externalState(value: ICNInputControlState) {
    this.data.state = value;
    this.cd.markForCheck();
  }

  @Output("onBlur") blurEmitter: EventEmitter<ICAutocompleteGenericModel | string> =
    new EventEmitter<ICAutocompleteGenericModel | string>();

  @Output("onSelect") selectEmitter: EventEmitter<ICAutocompleteGenericModel | string> =
    new EventEmitter<ICAutocompleteGenericModel | string>();

  @Output("onFocus") focusEmitter: EventEmitter<void> = new EventEmitter<void>();
  /**
   * replace autocomplete header with a label matching field group in design system
   */
  @Input("fieldsGroup") fieldsGroup: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    public element: ElementRef,
    public objectUtils: ObjectUtils,
    public translationService: TranslateService,
    public cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {}

  ngOnDestroy() {}

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: ICAutocompleteGenericModel | string): void {
    this.data.value = value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */
  public onFocus($event: any): void {
    this.inputFocused = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.focusEmitter.emit();
  }

  public onBlur($event: any): void {
    this.inputFocused = false;

    this.cd.markForCheck();

    if (this.validate()) {
      this.blurEmitter.emit(this.data.value);
    }
  }

  // for propagation
  public onSelect($event: any): void {
    this.cd.markForCheck();

    if (this.validate()) {
      this.selectEmitter.emit(this.data.value);
    }
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  /**********************************************************************/
  /* EXTERNAL API */
  public reset(): void {
    this.data.value = null;
    this.data.dataset = this.dataset;
    this.data.filteredItems = [];
    this.data.state = ICNInputControlState.INACTIVE;
  }

  public lock(): void {
    this.locked = true;
    this.cd.markForCheck();
  }

  public unlock(): void {
    this.locked = false;
    this.cd.markForCheck();
  }

  /**
   *
   * @returns true if this.data.value is an ICAutocompleteGenericModel
   * or null eligible for emit. e.g. _don't emit invalid unmatched search text_
   */
  public validate(): boolean {
    const value = this.data.value;

    if (value != null && typeof value === "object") {
      this.data.state = ICNInputControlState.VALID;
      return true;
    } else if (this.data.state === ICNInputControlState.INACTIVE) {
      return true;
    } else if ((value != null && value != "") || !this.allowEmpty) {
      this.data.state = ICNInputControlState.INVALID;
      return value == null;
    } else if (this.allowEmpty) {
      this.data.state = ICNInputControlState.INACTIVE;
      return true;
    }
  }

  /**********************************************************************/
  /* INTERNAL API */

  public search(event: any): void {
    let filteredItems = [];
    let allItems = this.data.dataset;

    for (let i = 0; i < allItems.length; i++) {
      let item = allItems[i];
      if (
        event.query.length === 0 ||
        item.label.toLowerCase().indexOf(event.query.toLowerCase()) >= 0
      ) {
        filteredItems.push(item);
      }
    }
    this.data.filteredItems = filteredItems;
  }
}
